import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useInvestigationStore } from '../store/authStore';
import { investigationService, transactionService, aiService } from '../services/api';
import SearchPanel from '../components/SearchPanel';
import { formatDateRelative, formatDate } from '../services/dateUtils';
import '../styles/Dashboard.css';

export default function Dashboard() {
  const navigate = useNavigate();
  const { token, username, isAdmin, role, logout } = useAuthStore();
  const { investigations, setInvestigations } = useInvestigationStore();
  const [showForm, setShowForm]               = useState(false);
  const [loading, setLoading]                 = useState(true);
  const [searchFilters, setSearchFilters]     = useState({});
  const [creating, setCreating]               = useState(false);
  const [fetchingTxs, setFetchingTxs]         = useState(false);
  const [lookingUpTx, setLookingUpTx]         = useState(false);
  const [txError, setTxError]                 = useState(null);
  const [transactions, setTransactions]       = useState([]);
  const [selectedTxIndex, setSelectedTxIndex] = useState(null);
  const [txPreview, setTxPreview]             = useState(null);
  const [inputMode, setInputMode]             = useState('seed_tx'); // 'seed_tx' | 'address' | 'tx'
  const [newInvestigation, setNewInvestigation] = useState({
    title: '', blockchain: 'tron', wallet_address: '', tx_hash: '', description: '', incident_date: '',
  });
  // ── Seed-TX-anchored creation (Aug 11 rework): paste the crime TX IDs -- the
  // transaction(s) that moved funds TO the scammer -- at creation time, same
  // paste/parse UX as TxLookupPanel.js's existing secondary tab, but wired into
  // creation itself so the graph is built ONLY from these TXs from the start,
  // no separate post-creation "promote" step and no broad address crawl. ──
  const [seedTxText, setSeedTxText] = useState('');

  // ── Dashboard AI Sidebar ──────────────────────────────────────────
  const [aiMessages, setAiMessages]     = useState([]);
  const [aiInput, setAiInput]           = useState('');
  const [aiLoading, setAiLoading]       = useState(false);
  const [aiStatus, setAiStatus]         = useState(null);
  const aiMessagesEndRef                = useRef(null);

  useEffect(() => {
    aiService.status()
      .then(res => setAiStatus(res.data))
      .catch(() => setAiStatus(null));
  }, []);

  useEffect(() => {
    aiMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages]);

  const handleAiSend = async () => {
    const q = aiInput.trim();
    if (!q || aiLoading) return;
    setAiInput('');
    setAiMessages(prev => [...prev, { role: 'user', content: q }]);
    setAiLoading(true);
    try {
      const res = await aiService.chat(q);
      const answer = res?.data?.answer || '[No response — check AI provider config in Claude/data/ai_settings.env]';
      setAiMessages(prev => [...prev, { role: 'ai', content: answer }]);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'AI request failed';
      setAiMessages(prev => [...prev, { role: 'error', content: msg }]);
    } finally {
      setAiLoading(false);
    }
  };

  const AI_SUGGESTIONS = [
    'What is a mixer in blockchain?',
    'How do I spot a scam wallet?',
    'What makes an address high risk?',
    'Explain USDT TRC20 contracts',
  ];

  const loadInvestigations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await investigationService.list();
      setInvestigations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load investigations:', error.response?.data || error.message);
      if (error.response?.status === 401) { logout(); navigate('/login'); }
    } finally {
      setLoading(false);
    }
  }, [setInvestigations, logout, navigate]);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    loadInvestigations();
  }, [token, loadInvestigations, navigate]);

  // Parse paste area into clean TX IDs — same parsing rule as TxLookupPanel.js's
  // parseTxIds so pasted lists behave identically in both places.
  const parseSeedTxIds = useCallback((text) => {
    return text
      .split(/[\n,;\s]+/)
      .map(s => s.trim())
      .filter(s => s.length > 10);
  }, []);
  const seedTxIds = parseSeedTxIds(seedTxText);

  // ── Fetch transactions for a wallet address ──────────────────────
  const handleFetchTransactions = async () => {
    if (!newInvestigation.wallet_address || !newInvestigation.blockchain) {
      setTxError('Please enter a wallet address and select a blockchain');
      return;
    }
    try {
      setFetchingTxs(true); setTxError(null);
      setTransactions([]); setSelectedTxIndex(null);
      const response = await transactionService.getForAddress(
        newInvestigation.blockchain, newInvestigation.wallet_address);
      const data = response.data;
      if (data && !Array.isArray(data) && data.detail) {
        setTxError(data.detail);
      } else if (Array.isArray(data)) {
        setTransactions(data);
      } else {
        setTxError('No transactions found for this address');
      }
    } catch (error) {
      setTxError(error.message || 'Failed to fetch transactions');
    } finally {
      setFetchingTxs(false);
    }
  };

  // ── Look up a single TX hash ──────────────────────────────────────
  const handleLookupTx = async () => {
    if (!newInvestigation.tx_hash || !newInvestigation.blockchain) {
      setTxError('Please enter a transaction hash and select a blockchain');
      return;
    }
    try {
      setLookingUpTx(true); setTxError(null); setTxPreview(null);
      const response = await transactionService.getByHash(
        newInvestigation.blockchain, newInvestigation.tx_hash);
      setTxPreview(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Transaction not found';
      setTxError(msg);
    } finally {
      setLookingUpTx(false);
    }
  };

  // ── Create investigation ──────────────────────────────────────────
  const handleCreateInvestigation = async () => {
    if (!newInvestigation.title) {
      alert('Please enter an investigation title'); return;
    }
    if (inputMode === 'address' && !newInvestigation.wallet_address) {
      alert('Please enter a wallet address'); return;
    }
    if (inputMode === 'tx' && !newInvestigation.tx_hash) {
      alert('Please enter a transaction hash'); return;
    }
    if (inputMode === 'seed_tx' && seedTxIds.length === 0) {
      alert('Paste at least one crime transaction ID'); return;
    }

    try {
      setCreating(true);
      const payload = {
        title:       newInvestigation.title,
        blockchain:  newInvestigation.blockchain,
        description: newInvestigation.description,
      };
      if (newInvestigation.incident_date) {
        payload.incident_date = newInvestigation.incident_date;
      }

      if (inputMode === 'seed_tx') {
        payload.seed_tx_ids = seedTxIds;
        if (newInvestigation.wallet_address) {
          payload.start_address = newInvestigation.wallet_address; // optional override; defaults server-side to the first seed TX's sender
        }
      } else if (inputMode === 'address') {
        if (selectedTxIndex !== null) {
          payload.start_address = transactions[selectedTxIndex].from_address
                                  || newInvestigation.wallet_address;
        } else {
          payload.start_address = newInvestigation.wallet_address;
        }
      } else {
        payload.start_tx_hash = newInvestigation.tx_hash;
        if (newInvestigation.wallet_address) {
          payload.start_address = newInvestigation.wallet_address;
        }
      }

      await investigationService.create(payload);
      handleResetForm();
      setShowForm(false);
      await loadInvestigations();
    } catch (error) {
      alert('Failed to create investigation: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreating(false);
    }
  };

  const handleResetForm = () => {
    setNewInvestigation({ title: '', blockchain: 'tron', wallet_address: '', tx_hash: '', description: '', incident_date: '' });
    setTransactions([]); setSelectedTxIndex(null);
    setTxPreview(null); setTxError(null);
    setSeedTxText('');
  };

  const handleModeSwitch = (mode) => {
    setInputMode(mode);
    setTransactions([]); setSelectedTxIndex(null);
    setTxPreview(null); setTxError(null);
  };

  const getTokenSymbol = () => {
    switch (newInvestigation.blockchain) {
      case 'tron':     return 'TRX';
      case 'ethereum': return 'ETH';
      case 'polygon':  return 'POL';
      case 'arbitrum': return 'ETH';
      case 'bsc':      return 'BNB';
      case 'bitcoin':  return 'BTC';
      case 'solana':   return 'SOL';
      default:         return '';
    }
  };

  const EXPLORER_BASE = {
    tron:     'https://tronscan.org/#',
    ethereum: 'https://etherscan.io',
    polygon:  'https://polygonscan.com',
    arbitrum: 'https://arbiscan.io',
    bsc:      'https://bscscan.com',
    bitcoin:  'https://blockstream.info',
    solana:   'https://solscan.io',
  };

  const explorerLink = (hash, type = 'tx') => {
    const bc   = newInvestigation.blockchain;
    const base = EXPLORER_BASE[bc] || EXPLORER_BASE.ethereum;
    if (bc === 'tron')
      return type === 'tx' ? `${base}/transaction/${hash}` : `${base}/address/${hash}`;
    if (bc === 'solana')
      return type === 'tx' ? `${base}/tx/${hash}` : `${base}/account/${hash}`;
    return type === 'tx' ? `${base}/tx/${hash}` : `${base}/address/${hash}`;
  };

  const TxCard = ({ tx, selected, onClick, index }) => (
    <div className={`tx-item ${selected ? 'selected' : ''}`} onClick={onClick}>
      <div className="tx-header">
        <strong>{index !== undefined ? `TX #${index + 1}` : 'Transaction'}</strong>
        <span className={`status ${tx.status}`}>{tx.status}</span>
      </div>
      <div className="tx-details-mini">
        <div><strong>Hash:</strong>
          <a href={explorerLink(tx.tx_hash)} target="_blank" rel="noopener noreferrer"
             className="link-hash" onClick={e => e.stopPropagation()}>{tx.tx_hash}</a>
        </div>
        <div><strong>From:</strong>
          <a href={explorerLink(tx.from_address, 'addr')} target="_blank" rel="noopener noreferrer"
             className="link-hash" onClick={e => e.stopPropagation()}>{tx.from_address}</a>
        </div>
        <div><strong>To:</strong>
          <a href={explorerLink(tx.to_address, 'addr')} target="_blank" rel="noopener noreferrer"
             className="link-hash" onClick={e => e.stopPropagation()}>{tx.to_address}</a>
        </div>
        <div><strong>Value:</strong> {(tx.value || 0).toFixed(4)} {tx.token_symbol || getTokenSymbol()}</div>
        {tx.timestamp && <div><strong>Time:</strong> {formatDate(tx.timestamp)}</div>}
      </div>
    </div>
  );

  if (!token) return null;

  // ── Filter investigations client-side based on SearchPanel filters ──
  const filteredInvestigations = investigations.filter(inv => {
    const { query, status, blockchain, date_from, date_to } = searchFilters;
    if (status     && inv.status    !== status)     return false;
    if (blockchain && inv.blockchain !== blockchain) return false;
    if (date_from  && new Date(inv.created_at) < new Date(date_from)) return false;
    if (date_to    && new Date(inv.created_at) > new Date(date_to))   return false;
    if (query) {
      const q = query.toLowerCase();
      return (
        inv.title?.toLowerCase().includes(q) ||
        inv.description?.toLowerCase().includes(q) ||
        inv.start_address?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // ── Open Statements in a separate browser window (own DB, separate module) ──
  const handleOpenStatements = () => {
    const left = (window.screenX ?? window.screenLeft ?? 0) + 60;
    const top  = (window.screenY ?? window.screenTop  ?? 0) + 60;
    const features = `noopener,noreferrer,width=1100,height=800,left=${left},top=${top}`;
    const w = window.open('/statements', '_blank', features);
    try { w?.focus(); } catch {}
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <h1>Noctis Ledger</h1>
          <p>Blockchain Investigation Platform</p>
        </div>
        <div className="header-right">
          <button onClick={() => navigate('/known-addresses')} className="known-addr-btn">
            🗂️ Known Addresses
          </button>
          <button onClick={handleOpenStatements} className="statements-btn">
            📄 Statements
          </button>
          {(role === 'admin' || role === 'superuser' || isAdmin) && (
            <button onClick={() => navigate('/admin/users')} className="known-addr-btn">
              👤 Admin
            </button>
          )}
          <span className="user-badge">
            {username} {isAdmin && <span className="admin-badge">ADMIN</span>}
          </span>
          <button onClick={() => { logout(); navigate('/login'); }} className="logout-btn">Logout</button>
        </div>
      </header>

      <main className="dashboard-main-layout">
        <div className="investigations-section">
          <div className="section-header">
            <h2>Investigations {filteredInvestigations.length !== investigations.length && <span className="filter-count">({filteredInvestigations.length} of {investigations.length})</span>}</h2>
            <button
              onClick={() => { if (showForm) handleResetForm(); setShowForm(!showForm); }}
              className="btn btn-primary"
            >
              {showForm ? 'Cancel' : '+ New Investigation'}
            </button>
          </div>

          <SearchPanel
            onSearch={setSearchFilters}
            onFilter={setSearchFilters}
          />

          {showForm && (
            <div className="investigation-form">

              {/* Title */}
              <div className="form-group">
                <label>Investigation Title *</label>
                <input type="text" placeholder="e.g., Track Suspicious Wallet Activity"
                  value={newInvestigation.title}
                  onChange={e => setNewInvestigation({ ...newInvestigation, title: e.target.value })}
                />
              </div>

              {/* Blockchain */}
              <div className="form-group">
                <label>Blockchain *</label>
                <select value={newInvestigation.blockchain}
                  onChange={e => {
                    setNewInvestigation({ ...newInvestigation, blockchain: e.target.value });
                    setTransactions([]); setSelectedTxIndex(null); setTxPreview(null);
                  }}>
                  <option value="tron">TRON</option>
                  <option value="ethereum">Ethereum</option>
                  <option value="polygon">Polygon</option>
                  <option value="arbitrum">Arbitrum</option>
                  <option value="bsc">BNB Chain (BSC)</option>
                  <option value="bitcoin">Bitcoin</option>
                  <option value="solana">Solana</option>
                </select>
              </div>

              {/* Mode toggle */}
              <div className="form-group">
                <label>Start From</label>
                <div className="mode-toggle">
                  <button type="button"
                    className={`toggle-btn ${inputMode === 'seed_tx' ? 'active' : ''}`}
                    onClick={() => handleModeSwitch('seed_tx')}>
                    🌱 Crime Transaction IDs
                  </button>
                  <button type="button"
                    className={`toggle-btn ${inputMode === 'address' ? 'active' : ''}`}
                    onClick={() => handleModeSwitch('address')}>
                    🏠 Wallet Address (Advanced)
                  </button>
                  <button type="button"
                    className={`toggle-btn ${inputMode === 'tx' ? 'active' : ''}`}
                    onClick={() => handleModeSwitch('tx')}>
                    🔗 Transaction Hash (Advanced)
                  </button>
                </div>
              </div>

              {/* Seed TX mode — Aug 11 rework: build the graph ONLY from the crime
                  TX(s) that moved funds to the scammer, no broad address crawl */}
              {inputMode === 'seed_tx' && (
                <div className="form-group">
                  <label>
                    Crime Transaction IDs *
                    <span className="optional">
                      {seedTxIds.length > 0 ? ` — ${seedTxIds.length} detected` : ' — paste one per line'}
                    </span>
                  </label>
                  <textarea
                    placeholder={'Paste the TX ID(s) that moved funds TO the scammer, one per line:\n\nTV1zeU4bK92Fs...\n0x4a9f8c2b1d...'}
                    value={seedTxText}
                    onChange={e => setSeedTxText(e.target.value)}
                    rows={6}
                    spellCheck={false}
                  />
                  <p className="field-hint">
                    Only these transaction(s) — and whatever they touch — will appear on the graph.
                    Everything else in either wallet's history is excluded as noise. You can add more
                    later from the investigation's Seed Transactions tab.
                  </p>
                  <label style={{ marginTop: 10 }}>Victim Address Override <span className="optional">(optional)</span></label>
                  <input type="text"
                    placeholder="Leave blank to use the sender of the first crime TX"
                    value={newInvestigation.wallet_address}
                    onChange={e => setNewInvestigation({ ...newInvestigation, wallet_address: e.target.value })}
                  />
                </div>
              )}

              {/* Address mode */}
              {inputMode === 'address' && (
                <div className="form-group">
                  <label>Wallet Address *</label>
                  <div className="input-row">
                    <input type="text"
                      placeholder={
                        newInvestigation.blockchain === 'tron' ? 'T... (TRON address)' :
                        newInvestigation.blockchain === 'bitcoin' ? '1... / 3... / bc1... (Bitcoin address)' :
                        newInvestigation.blockchain === 'bsc' ? '0x... (BSC / BNB Chain address)' :
                        newInvestigation.blockchain === 'solana' ? 'Base58 (Solana address)' :
                        '0x... (address)'
                      }
                      value={newInvestigation.wallet_address}
                      onChange={e => setNewInvestigation({ ...newInvestigation, wallet_address: e.target.value })}
                    />
                    <button type="button" className="btn btn-secondary"
                      onClick={handleFetchTransactions}
                      disabled={fetchingTxs || !newInvestigation.wallet_address}>
                      {fetchingTxs ? '⏳ Fetching...' : '🔍 Fetch Transactions'}
                    </button>
                  </div>
                  <p className="field-hint">Optionally fetch transactions to preview before creating, or create directly from the address.</p>
                </div>
              )}

              {/* TX hash mode */}
              {inputMode === 'tx' && (
                <>
                  <div className="form-group">
                    <label>Transaction Hash *</label>
                    <div className="input-row">
                      <input type="text"
                        placeholder={
                          newInvestigation.blockchain === 'tron' ? 'Transaction ID...' :
                          newInvestigation.blockchain === 'bitcoin' ? 'Transaction ID (txid)...' :
                          newInvestigation.blockchain === 'solana' ? 'Transaction signature...' :
                          '0x...'
                        }
                        value={newInvestigation.tx_hash}
                        onChange={e => setNewInvestigation({ ...newInvestigation, tx_hash: e.target.value })}
                      />
                      <button type="button" className="btn btn-secondary"
                        onClick={handleLookupTx}
                        disabled={lookingUpTx || !newInvestigation.tx_hash}>
                        {lookingUpTx ? '⏳ Looking up...' : '🔍 Look Up TX'}
                      </button>
                    </div>
                    <p className="field-hint">The investigation will start from the sender address of this transaction.</p>
                  </div>
                  <div className="form-group">
                    <label>Override Start Address <span className="optional">(optional)</span></label>
                    <input type="text"
                      placeholder="Leave blank to use sender address from TX"
                      value={newInvestigation.wallet_address}
                      onChange={e => setNewInvestigation({ ...newInvestigation, wallet_address: e.target.value })}
                    />
                  </div>
                </>
              )}

              {/* Incident date — when the scam actually happened, NOT when this record
                  was created. Available regardless of creation mode. */}
              <div className="form-group">
                <label>Incident Date <span className="optional">(optional — when the scam actually happened)</span></label>
                <input type="date"
                  value={newInvestigation.incident_date}
                  onChange={e => setNewInvestigation({ ...newInvestigation, incident_date: e.target.value })}
                />
                <p className="field-hint">Defaults to unset if left blank. Distinct from the case's creation timestamp — editable later from the investigation page.</p>
              </div>

              {/* Description */}
              <div className="form-group">
                <label>Description <span className="optional">(optional)</span></label>
                <textarea placeholder="Add case notes..."
                  value={newInvestigation.description}
                  onChange={e => setNewInvestigation({ ...newInvestigation, description: e.target.value })}
                />
              </div>

              {txError && <div className="error-message">⚠ {txError}</div>}

              {/* TX preview (hash mode) */}
              {inputMode === 'tx' && txPreview && (
                <div className="tx-list">
                  <h4>✓ Transaction Found</h4>
                  <TxCard tx={txPreview} selected={false} />
                  <p className="field-hint">Investigation will start from: <strong>{txPreview.from_address}</strong></p>
                </div>
              )}

              {/* TX list (address mode) */}
              {inputMode === 'address' && transactions.length > 0 && (
                <div className="tx-list">
                  <h4>Select a Starting Transaction <span className="optional">(optional)</span></h4>
                  <div className="tx-items">
                    {transactions.map((tx, idx) => (
                      <TxCard key={tx.tx_hash || idx} tx={tx} index={idx}
                        selected={selectedTxIndex === idx}
                        onClick={() => setSelectedTxIndex(selectedTxIndex === idx ? null : idx)}
                      />
                    ))}
                  </div>
                </div>
              )}

              <button type="button" className="btn btn-success"
                onClick={handleCreateInvestigation}
                disabled={creating || !newInvestigation.title}>
                {creating ? '⏳ Creating...' : '✓ Create Investigation'}
              </button>
            </div>
          )}

          {loading ? (
            <p>Loading investigations...</p>
          ) : (
            <div className="investigations-list">
              {investigations.length === 0 && !showForm && (
                <div className="empty-state">
                  <p>No investigations yet.</p>
                  <p>Click <strong>+ New Investigation</strong> to get started.</p>
                </div>
              )}
              {investigations.length > 0 && filteredInvestigations.length === 0 && (
                <div className="empty-state">
                  <p>No investigations match your search.</p>
                </div>
              )}
              {filteredInvestigations.map(inv => (
                <div key={inv.id} className="investigation-card"
                  onClick={() => navigate(`/investigation/${inv.id}`)}>
                  <h3>{inv.title}</h3>
                  <p>{inv.description}</p>
                  <div className="card-meta">
                    <span className="badge">{inv.blockchain.toUpperCase()}</span>
                    <span className="addr-preview">{inv.start_address?.slice(0, 16)}...</span>
                    {inv.incident_date && (
                      <span className="card-date" title="Incident date">🔴 {formatDate(inv.incident_date)}</span>
                    )}
                    <span className="card-date">{formatDateRelative(inv.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── AI Sidebar ── */}
        <aside className="dashboard-ai-sidebar">
          <div className="ai-sidebar-header">
            <div className="ai-sidebar-title">
              <span className="ai-sidebar-icon">🤖</span>
              <h3>AI Assistant</h3>
            </div>
            <div className={`ai-status-pill ${aiStatus?.key_set ? 'connected' : 'disconnected'}`}>
              <span className="status-dot" />
              {aiStatus?.key_set ? `${aiStatus.provider} · ${aiStatus.model}` : 'Not configured'}
            </div>
          </div>

          <div className="ai-sidebar-messages">
            {aiMessages.length === 0 ? (
              <div className="ai-sidebar-empty">
                <div style={{ fontSize: 32, marginBottom: 8 }}>🧠</div>
                <p>Ask anything about blockchain forensics</p>
                <div className="ai-suggestions">
                  {AI_SUGGESTIONS.map(s => (
                    <button key={s} className="suggestion-chip"
                      onClick={() => { setAiInput(s); }}>{s}</button>
                  ))}
                </div>
              </div>
            ) : (
              aiMessages.map((msg, i) => (
                <div key={i} className={`ai-sidebar-msg ${msg.role}`}>
                  <span className="ai-sidebar-msg-label">
                    {msg.role === 'user' ? 'You' : msg.role === 'error' ? '⚠ Error' : '🤖 AI'}
                  </span>
                  <div className="ai-sidebar-msg-content">{msg.content}</div>
                </div>
              ))
            )}
            {aiLoading && (
              <div className="ai-sidebar-msg ai">
                <span className="ai-sidebar-msg-label">🤖 AI</span>
                <div className="ai-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}
            <div ref={aiMessagesEndRef} />
          </div>

          <div className="ai-sidebar-input-row">
            <textarea
              className="ai-sidebar-input"
              placeholder="Ask about blockchain, addresses, risk..."
              value={aiInput}
              onChange={e => setAiInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiSend(); } }}
              rows={2}
              disabled={aiLoading}
            />
            <button className="ai-send-btn" onClick={handleAiSend} disabled={aiLoading || !aiInput.trim()}>
              ➤
            </button>
          </div>
        </aside>

      </main>
    </div>
  );
}
